import { z } from "zod";

import { apiDelete, apiGet, apiPost, apiPostFormData, apiPut } from "../lib/api";

// ─── Core target record ─────────────────────────────────────────────────────────

export const TargetListItemSchema = z.object({
  target_id: z.string().uuid(),
  full_name: z.string(),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  primary_phone_e164: z.string().nullable().optional(),
  primary_email: z.string().nullable().optional(),
  role: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  // Operator self-test pool (Phase 1). test_only targets bypass ROE on the
  // test-call path ONLY once phone_verified; never dialable by a real campaign.
  test_only: z.boolean().default(false),
  phone_verified: z.boolean().default(false),
  phone_verified_at: z.string().nullable().optional(),
  // Target-directory enrichment from the group registry. Default [] so older
  // responses (or single-target endpoints that reuse this schema) still parse.
  group_ids: z.array(z.string()).default([]),
  group_names: z.array(z.string()).default([]),
});
export type TargetListItem = z.infer<typeof TargetListItemSchema>;

// ─── Phone verification (test-pool OTP) ──────────────────────────────────────────

export const VerifyStartResponseSchema = z.object({
  started: z.boolean(),
});

export const VerifyConfirmResponseSchema = z.object({
  verified: z.boolean(),
  reason: z.string().nullable().optional(),
});
export type VerifyConfirmResult = z.infer<typeof VerifyConfirmResponseSchema>;

// ─── List response ──────────────────────────────────────────────────────────────

export const TargetListResponseSchema = z.object({
  items: z.array(TargetListItemSchema),
  total: z.number(),
});
export type TargetListResponse = z.infer<typeof TargetListResponseSchema>;

// Single-target endpoints (get/create/update/by-phone) return the record
// wrapped: { record: <target> }. Matches the backend TargetDetailResponse DTO.
export const TargetDetailResponseSchema = z.object({
  record: TargetListItemSchema,
});

// ─── Facets (distinct department/role values for filter dropdowns) ────────────────
// Mirrors console_api TargetFacetsResponse: distinct, non-null, sorted lists.
export const TargetFacetsResponseSchema = z.object({
  departments: z.array(z.string()),
  roles: z.array(z.string()),
});
export type TargetFacetsResponse = z.infer<typeof TargetFacetsResponseSchema>;

// ─── CSV import preview ─────────────────────────────────────────────────────────

export const CsvImportPreviewSchema = z.object({
  preview_token: z.string(),
  valid: z.array(z.record(z.string(), z.string())),
  duplicates: z.array(z.record(z.string(), z.string())),
  invalid: z.array(
    z.object({ row_number: z.number(), raw: z.string(), reason: z.string() }),
  ),
  total_rows: z.number(),
});
export type CsvImportPreview = z.infer<typeof CsvImportPreviewSchema>;

// ─── Create/update body ─────────────────────────────────────────────────────────

export interface TargetWriteBody {
  full_name: string;
  first_name?: string | null;
  last_name?: string | null;
  primary_phone_e164?: string | null;
  primary_email?: string | null;
  role?: string | null;
  department?: string | null;
  // Only accepted on create; the backend ignores it on update.
  test_only?: boolean;
  // Only accepted on create; when set, the new target is added to this group.
  group_id?: string | null;
}

// ─── List filters (AND-combined facet query for GET /api/targets) ─────────────────
export interface TargetFilters {
  q?: string;
  department?: string;
  role?: string;
  test_only?: boolean;
  verified?: boolean;
  ungrouped?: boolean;
}

// ─── API fetchers ───────────────────────────────────────────────────────────────

// Exported for unit testing. Empty/undefined fields are omitted — the backend
// treats a missing param as "no filter for that field".
export function buildTargetsQuery(filters?: string | TargetFilters): string {
  const f: TargetFilters =
    typeof filters === "string" ? { q: filters } : filters ?? {};
  const params = new URLSearchParams();
  if (f.q) params.set("q", f.q);
  if (f.department) params.set("department", f.department);
  if (f.role) params.set("role", f.role);
  if (f.test_only !== undefined) params.set("test_only", String(f.test_only));
  if (f.verified !== undefined) params.set("verified", String(f.verified));
  if (f.ungrouped !== undefined) params.set("ungrouped", String(f.ungrouped));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function fetchTargets(filters?: string | TargetFilters) {
  return apiGet(`/api/targets${buildTargetsQuery(filters)}`, TargetListResponseSchema);
}

/** Distinct department/role values to populate the "Add existing" filter dropdowns. */
export function fetchTargetFacets() {
  return apiGet("/api/targets/facets", TargetFacetsResponseSchema);
}

export async function fetchTargetByPhone(phone: string) {
  const { record } = await apiGet(
    `/api/targets/by-phone/${encodeURIComponent(phone)}`,
    TargetDetailResponseSchema,
  );
  return record;
}

export async function createTarget(body: TargetWriteBody) {
  const { record } = await apiPost("/api/targets", TargetDetailResponseSchema, body);
  return record;
}

export async function updateTarget(id: string, body: Partial<TargetWriteBody>) {
  const { record } = await apiPut(
    `/api/targets/${id}`,
    TargetDetailResponseSchema,
    body,
  );
  return record;
}

/** Hard-delete a target. Cascades its group memberships server-side. 204. */
export function deleteTarget(id: string) {
  return apiDelete(`/api/targets/${id}`);
}

/** Place the TTS-only verification call for a test_only target. */
export function verifyStart(id: string) {
  return apiPost(
    `/api/targets/${id}/verify/start`,
    VerifyStartResponseSchema,
    undefined,
  );
}

/** Submit the spoken OTP code. Returns { verified, reason? } on both pass + fail. */
export function verifyConfirm(id: string, code: string) {
  return apiPost(
    `/api/targets/${id}/verify/confirm`,
    VerifyConfirmResponseSchema,
    { code },
  );
}

export function previewTargetsImport(file: File) {
  const fd = new FormData();
  fd.append("file", file);
  return apiPostFormData(
    "/api/targets/import/preview",
    CsvImportPreviewSchema,
    fd,
  );
}

// POST /import/commit returns a BARE ARRAY of { record } — not the
// { items, total } envelope the list endpoint uses. This was parsed with
// TargetListResponseSchema, so a SUCCESSFUL import threw a Zod error: the
// targets were created, the operator saw a failure, re-imported, and got
// duplicates. Server-side tests pin the array shape
// (console-api/tests/test_targets.py:303-305), so the client was the wrong side.
export const TargetImportCommitResponseSchema = z.array(TargetDetailResponseSchema);

export function commitTargetsImport(preview_token: string) {
  return apiPost(
    "/api/targets/import/commit",
    TargetImportCommitResponseSchema,
    { preview_token },
  );
}
